import { Claim, PayableTimeCap } from '@scale/shared/types/queue';
import { useQuery } from '@tanstack/react-query-v4';
import { AssignmentJson } from '@scale/shared/assignmentJson/types/AssignmentJson';
import { fetchApi } from 'react-scale/redux/actions';
import { useTimerData } from 'react-scale/components/scalers/withV2Timer';
import { useEffect } from 'react';
import * as queryString from 'query-string';
import logger from '@scale/frontend-lib/logging';
import { usePauseCapability } from 'react-scale/components/scalers/tasks/common/PauseCapabilityHook';
import { useUnleashFeatureFlagWithLoading } from 'react-scale/hooks/useUnleashFeatureFlag';

/**
 * Helper hook to resolve the Claim info with fallback defaults to use.
 *
 * This provides claim timing data including maxClaim (submission deadline)
 * and claimedUntil (idle timeout) dates.
 *
 * When taskId is provided, it'll also apply pause time extension compensation (if any) to both
 * claimedUntil and maxClaim:
 * - Adds currently used pause idle time to extend deadlines during pause
 * - Subtracts borrowed extension time that was pre-added at pause start
 *
 * The compensation ensures that deadline displays remain consistent and transparent to users
 * during pause sessions, showing the effective deadline they will have after resuming.
 *
 * @param claim - The claim object containing timing information
 * @param projectId - Project ID for backend claim validation
 * @param assignment - Assignment data for backend claim validation
 * @param taskId - Task ID for pause state detection and compensation (optional)
 * @returns Object containing compensated timing data
 */
export function useClaimTime(
  claim?: Claim,
  projectId?: string,
  assignment?: AssignmentJson,
  taskId?: string,
): {
  maxClaim: Date;
  idleMinutes: number;
  claimedAt: Date | undefined;
  claimedUntil: Date;
  claimMinutes: number;
  now: Date;
  payableTime?: PayableTimeCap;
} {
  // NOTE: We need to use a future date to avoid dueDateModal flashes until we don't have the BE response
  const tomorrow = new Date();
  tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);

  const { checkedAtClientTime, checkedAt, idleMinutes, maxClaim, payableTime, claimMinutes } =
    Object.assign(
      {
        checkedAtClientTime: new Date(),
        checkedAt: new Date(),
        maxClaim: tomorrow,
        idleMinutes: 1,
        claimMinutes: 60 * 24, // 24 hours
      },
      claim,
    );
  const now = new Date(Date.now() - checkedAtClientTime.getTime() + checkedAt.getTime());

  const rawClaimedUntil = claim?.claimedUntil || new Date();

  // TODO: this should be moved to the backend and be returned via `usePayRate`
  const [timerData] = useTimerData();
  const timeSpent = timerData.serverTimeSpent;

  const { data } = useFetchCheckClaimBackend(
    !!projectId, // only fetch if projectId is available
    assignment,
    timeSpent,
  );

  const frontendCalculationMaxClaimTimestamp = maxClaim.getTime();
  let backendMaxClaim: Date | undefined;
  let backendMaxClaimTimestamp: number | undefined;
  let claimedAt: Date | undefined;

  if (projectId && data?.tasks && data?.tasks?.length) {
    const [task] = data.tasks;
    if (task && task.maxClaim) {
      backendMaxClaim = new Date(task.maxClaim);
      backendMaxClaimTimestamp = backendMaxClaim.getTime();
    }
    if (task && (task as { claimedAt?: string }).claimedAt) {
      claimedAt = new Date((task as { claimedAt: string }).claimedAt);
    }
  }

  // check and log if the max claim is different between the frontend and the backend
  useEffect(() => {
    if (!backendMaxClaimTimestamp || !frontendCalculationMaxClaimTimestamp) {
      return;
    }
    const deltaSeconds =
      Math.abs(frontendCalculationMaxClaimTimestamp - backendMaxClaimTimestamp) / 1000;

    if (deltaSeconds > 60) {
      logger.info(`Frontend and backend max claim mismatch, by ${deltaSeconds} seconds`, {
        frontendMaxClaim: new Date(frontendCalculationMaxClaimTimestamp),
        backendMaxClaim: new Date(backendMaxClaimTimestamp),
        deltaSeconds,
        assignmentId: assignment?.assignmentId,
        projectId,
      });
    }
  }, [
    projectId,
    assignment?.assignmentId,
    backendMaxClaimTimestamp,
    frontendCalculationMaxClaimTimestamp,
  ]);

  // check paused state for compensation
  const { isPaused, pausedAt, pauseTimeClaimTimeExtensionInfo } = usePauseCapability(taskId ?? '');

  // pause claim time extension feature flag
  const { data: pauseTimerClaimTimeExtensionFeatureEnabled } = useUnleashFeatureFlagWithLoading(
    'pause-timer-claim-time-extension',
    {},
    false,
  );

  // use backend max claim if available, fallback to frontend/default maxClaim (in 24h)
  const rawMaxClaim = backendMaxClaim ?? maxClaim;

  // Apply pause compensation to both maxClaim and claimedUntil
  let compensatedMaxClaim = rawMaxClaim;
  let compensatedClaimedUntil = rawClaimedUntil;

  if (
    taskId &&
    isPaused &&
    pauseTimerClaimTimeExtensionFeatureEnabled &&
    pauseTimeClaimTimeExtensionInfo
  ) {
    const initialRemainingIdleTimeSeconds = Math.max(
      0,
      pauseTimeClaimTimeExtensionInfo.maxAvailableSeconds -
        pauseTimeClaimTimeExtensionInfo.usedSeconds,
    );

    if (pauseTimeClaimTimeExtensionInfo.maxAvailableSeconds > 0) {
      const currentTime = new Date();
      const pausedAtDate = new Date(pausedAt);
      const pausedDurationSecs = (currentTime.getTime() - pausedAtDate.getTime()) / 1000;

      const currentRemainingIdleTimeSeconds = Math.max(
        0,
        initialRemainingIdleTimeSeconds - pausedDurationSecs,
      );
      const currentPauseUsedIdleTimeSeconds =
        initialRemainingIdleTimeSeconds - currentRemainingIdleTimeSeconds;

      // apply pause extension compensation logic to both max claim dates
      const compensationMs =
        // forward compensation: add currently used pause idle time
        currentPauseUsedIdleTimeSeconds * 1000 -
        // backward compensation: hide borrowed claim time extension (pre-added at Pause start), for transparency
        (pauseTimeClaimTimeExtensionInfo?.borrowedExtensionSeconds || 0) * 1000;

      compensatedMaxClaim = new Date(rawMaxClaim.getTime() + compensationMs);
      compensatedClaimedUntil = new Date(rawClaimedUntil.getTime() + compensationMs);
    }
  }

  return {
    maxClaim: compensatedMaxClaim,
    idleMinutes,
    claimedUntil: compensatedClaimedUntil,
    claimMinutes,
    claimedAt,
    now,
    payableTime,
  };
}

function useFetchCheckClaimBackend(
  enabled: boolean,
  currentAssignment: AssignmentJson | undefined,
  elapsedTime: number,
) {
  return useQuery({
    queryKey: ['checkClaim', currentAssignment?.assignmentId],
    queryFn: async () => {
      const { forceClaim } = queryString.parse(window.location.search);
      const usingForceClaimLink = forceClaim === '1';

      const body = {
        stage: undefined,
        task: { id: currentAssignment?.assignmentId },
        attempt: undefined,
        elapsedTime,
        usingForceClaimLink,
      };

      return await fetchApi('/tasks/check_claim', { method: 'POST', body }).then(res => res.json());
    },
    enabled: enabled && !!currentAssignment?.assignmentId,
    refetchOnMount: false,
    refetchInterval: 60000, // refetch every minute
  });
}
